import * as React from 'react';
import { useState, useEffect } from 'react';
import {
    Card,
    CardHeader,
    CardContent,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Box,
    Skeleton,
    TextField,
    Grid,
    Typography,
} from '@mui/material';
import { format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear } from 'date-fns';
import axios from 'axios';
import useAuthorize from '../hooks/useAuthorize';
import { MARKETING_DASHBOARD_ROLES } from '../roles/portalRoles';
import { AnalyticsFreshnessNote } from '../utils/AnalyticsFreshnessNote';
import SessionMetricChart from './SessionMetricChart';
import SessionQualityCards from './SessionQualityCards';

const DashboardSessions = () => {
    useAuthorize(MARKETING_DASHBOARD_ROLES);

    // Default date range: first day of the current year through today.
    const getDefaultDateRange = () => {
        const now = new Date();
        const startDate = startOfYear(now);

        return {
            fromDate: format(startDate, 'yyyy-MM-dd'),
            toDate: format(now, 'yyyy-MM-dd'),
        };
    };

    const defaultDates = getDefaultDateRange();

    const [period, setPeriod] = useState<'daily' | 'weekly' | 'monthly' | 'quarterly'>('monthly');
    const [fromDate, setFromDate] = useState<string>(defaultDates.fromDate);
    const [toDate, setToDate] = useState<string>(defaultDates.toDate);
    const [quickRange, setQuickRange] = useState<string>('');

    const [sessionTrendData, setSessionTrendData] = useState<any[]>([]);
    const [sessionQualityData, setSessionQualityData] = useState<any | null>(null);
    const [activeUserData, setActiveUserData] = useState<any | null>(null);
    const [sessionTrendLoading, setSessionTrendLoading] = useState(true);
    const [sessionQualityLoading, setSessionQualityLoading] = useState(true);
    const [dataThrough, setDataThrough] = useState<string | null>(null);
    const [lastUpdatedAt, setLastUpdatedAt] = useState<string | null>(null);

    useEffect(() => {
        const fetchSessionAnalytics = async () => {
            setSessionTrendLoading(true);
            setSessionQualityLoading(true);
            const apiUrl = `${process.env.REACT_APP_API_URL}/customers/session-analytics`;
            const params = {
                period: period,
                from_date: fromDate,
                to_date: toDate,
            };

            console.log('🔍 Fetching session analytics with params:', { apiUrl, params });

            try {
                const response = await axios.get(apiUrl, { params });
                console.log('✅ Session analytics response:', response.data);
                setDataThrough(response.data?.data_through || response.headers?.['x-analytics-data-through'] || null);
                setLastUpdatedAt(response.data?.last_updated_at || response.headers?.['x-analytics-last-updated'] || null);
                setSessionTrendData(response.data?.trends || []);
                setSessionQualityData({
                    entrance_pages: response.data?.entrance_pages || [],
                    exit_pages: response.data?.exit_pages || [],
                    active_vs_idle: response.data?.active_vs_idle || { active_sessions: 0, idle_sessions: 0 },
                    landing_page_quality: response.data?.landing_page_quality || [],
                    traffic_source_quality: response.data?.traffic_source_quality || [],
                    duration_buckets: response.data?.duration_buckets || [],
                    engagement_heatmap: response.data?.engagement_heatmap || [],
                    active_now: response.data?.active_now || 0,
                    active_now_window_minutes: response.data?.active_now_window_minutes || 30,
                });
                setActiveUserData(response.data?.active_users || null);
            } catch (error: any) {
                console.error('❌ Error fetching session analytics:', error);
                setSessionTrendData([]);
                setSessionQualityData(null);
                setActiveUserData(null);
                setDataThrough(null);
                setLastUpdatedAt(null);
            } finally {
                setSessionTrendLoading(false);
                setSessionQualityLoading(false);
            }
        };

        fetchSessionAnalytics();
    }, [period, fromDate, toDate]);

    const handlePeriodChange = (event: any) => {
        setPeriod(event.target.value);
    };

    const handleFromDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFromDate(event.target.value);
        setQuickRange(''); // Reset quick range when manually changing dates
    };

    const handleToDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setToDate(event.target.value);
        setQuickRange(''); // Reset quick range when manually changing dates
    };

    const handleQuickRangeChange = (event: any) => {
        const range = event.target.value;
        setQuickRange(range);
        
        if (!range) {
            return; // Don't update dates if "Custom" is selected
        }

        const now = new Date();
        let from = now;
        let to = now;

        switch (range) {
            case 'today':
                from = to = now;
                break;
            case 'yesterday':
                from = to = subDays(now, 1);
                break;
            case 'last7':
                from = subDays(now, 6);
                to = now;
                break;
            case 'thisWeek':
                from = startOfWeek(now, { weekStartsOn: 1 }); // Monday start
                to = endOfWeek(now, { weekStartsOn: 1 });
                break;
            case 'thisMonth':
                from = startOfMonth(now);
                to = endOfMonth(now);
                break;
            default:
                break;
        }

        setFromDate(format(from, 'yyyy-MM-dd'));
        setToDate(format(to, 'yyyy-MM-dd'));
    };

    const formatMetricDate = (date?: string) => {
        if (!date) {
            return '';
        }
        return format(new Date(`${date}T00:00:00`), 'MMM d, yyyy');
    };

    const renderTopMetricCard = (
        title: string,
        value: string | number,
        description: string,
        detail?: string,
        loading?: boolean,
    ) => (
        <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent sx={{ height: '100%' }}>
                {loading ? (
                    <Skeleton height={118} />
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, height: '100%' }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700 }}>
                            {title}
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 500, lineHeight: 1.1 }}>
                            {value}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {description}
                        </Typography>
                        {detail && (
                            <Typography variant="caption" color="text.secondary" sx={{ marginTop: 'auto' }}>
                                {detail}
                            </Typography>
                        )}
                    </Box>
                )}
            </CardContent>
        </Card>
    );

    return (
        <Box sx={{ padding: 2 }}>
            <Grid container spacing={2} sx={{ marginBottom: 2 }}>
                <Grid item xs={12} sm={6} lg={3}>
                    {renderTopMetricCard(
                        'Active users',
                        (sessionQualityData?.active_now || 0).toLocaleString(),
                        `Visitors with recent activity in the last ${sessionQualityData?.active_now_window_minutes || 30} minutes.`,
                        'Real-time activity',
                        sessionQualityLoading,
                    )}
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                    {renderTopMetricCard(
                        'DAU',
                        (activeUserData?.dau || 0).toLocaleString(),
                        'Unique visitors active today.',
                        `Daily active users on ${formatMetricDate(activeUserData?.dau_date)}`,
                        sessionTrendLoading,
                    )}
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                    {renderTopMetricCard(
                        'MAU',
                        (activeUserData?.mau || 0).toLocaleString(),
                        'Unique visitors active in the trailing 30-day window.',
                        `Monthly active users from ${formatMetricDate(activeUserData?.mau_start_date)} to ${formatMetricDate(activeUserData?.mau_end_date)}`,
                        sessionTrendLoading,
                    )}
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                    {renderTopMetricCard(
                        'DAU / MAU',
                        `${activeUserData?.dau_mau_ratio_percent || 0}%`,
                        'Stickiness ratio: daily active users divided by monthly active users.',
                        'Higher means more monthly users are returning today',
                        sessionTrendLoading,
                    )}
                </Grid>
            </Grid>

            <Card>
                <CardHeader
                    title="Session Analytics"
                    subheader="Shows how people use the website during the selected date range: how many sessions happened, how many pages visitors viewed, how long they stayed, and how often they left after one page."
                />
                <CardContent>
                    <Grid container spacing={2} sx={{ marginBottom: 2 }}>
                        <Grid item xs={12} sm={3}>
                            <TextField
                                label="From Date"
                                type="date"
                                value={fromDate}
                                onChange={handleFromDateChange}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <TextField
                                label="To Date"
                                type="date"
                                value={toDate}
                                onChange={handleToDateChange}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <FormControl variant="outlined" fullWidth size="small">
                                <InputLabel>Period</InputLabel>
                                <Select value={period} onChange={handlePeriodChange} label="Period">
                                    <MenuItem value="daily">Daily</MenuItem>
                                    <MenuItem value="weekly">Weekly</MenuItem>
                                    <MenuItem value="monthly">Monthly</MenuItem>
                                    <MenuItem value="quarterly">Quarterly</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <FormControl variant="outlined" fullWidth size="small">
                                <InputLabel>Quick Range</InputLabel>
                                <Select value={quickRange} onChange={handleQuickRangeChange} label="Quick Range">
                                    <MenuItem value="">Custom</MenuItem>
                                    <MenuItem value="today">Today</MenuItem>
                                    <MenuItem value="yesterday">Yesterday</MenuItem>
                                    <MenuItem value="last7">Last 7 Days</MenuItem>
                                    <MenuItem value="thisWeek">This Week</MenuItem>
                                    <MenuItem value="thisMonth">This Month</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>

                    {dataThrough && (
                        <AnalyticsFreshnessNote dataThrough={dataThrough} lastUpdatedAt={lastUpdatedAt} />
                    )}

                    {sessionTrendLoading ? (
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Skeleton height={300} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Skeleton height={300} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Skeleton height={300} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Skeleton height={300} />
                            </Grid>
                        </Grid>
                    ) : sessionTrendData.length === 0 ? (
                        <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <p>No session data available for the selected date range</p>
                        </Box>
                    ) : (
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Card variant="outlined">
                                    <CardHeader
                                        title="Total Sessions"
                                        subheader="Number of website sessions started during each selected period."
                                    />
                                    <CardContent>
                                        <SessionMetricChart
                                            data={sessionTrendData}
                                            period={period}
                                            metric="sessions"
                                            title="Total Sessions"
                                            chartType="bar"
                                            color="#1890ff"
                                            yAxisName="Sessions"
                                            showTitle={false}
                                        />
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Card variant="outlined">
                                    <CardHeader
                                        title="Average Pages per Session"
                                        subheader="Average number of pages a visitor viewed in one session."
                                    />
                                    <CardContent>
                                        <SessionMetricChart
                                            data={sessionTrendData}
                                            period={period}
                                            metric="avgPages"
                                            title="Avg Pages per Session"
                                            chartType="line"
                                            color="#52c41a"
                                            yAxisName="Pages"
                                            showTitle={false}
                                        />
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Card variant="outlined">
                                    <CardHeader
                                        title="Average Session Duration"
                                        subheader="Average time visitors spent on the website during one session."
                                    />
                                    <CardContent>
                                        <SessionMetricChart
                                            data={sessionTrendData}
                                            period={period}
                                            metric="avgDuration"
                                            title="Avg Session Duration"
                                            chartType="line"
                                            color="#fa8c16"
                                            yAxisName="Duration (seconds)"
                                            formatter={(val) => `${Math.round(val)}s`}
                                            showTitle={false}
                                        />
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Card variant="outlined">
                                    <CardHeader
                                        title="Bounce Rate"
                                        subheader="Percentage of sessions where the visitor left after viewing only one page."
                                    />
                                    <CardContent>
                                        <SessionMetricChart
                                            data={sessionTrendData}
                                            period={period}
                                            metric="bounceRate"
                                            title="Bounce Rate"
                                            chartType="line"
                                            color="#f5222d"
                                            yAxisName="Percentage (%)"
                                            formatter={(val) => `${val.toFixed(1)}%`}
                                            showTitle={false}
                                        />
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    )}
                </CardContent>
            </Card>

            <SessionQualityCards
                data={sessionQualityData}
                trendData={sessionTrendData}
                period={period}
                loading={sessionQualityLoading}
            />
        </Box>
    );
};

export default DashboardSessions;
